import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { stripeSyncedStatusAllowsNexusMainApp } from '../../lib/subscription';
import { setBusinessHomeScreen, CONTROL_CENTRE_APP_ID, CONTROL_CENTRE_ROUTE } from '../../lib/controlCentreHome';
import {
  Building2,
  Settings,
  CheckCircle,
  ArrowRight,
  ChevronLeft,
  Bot,
  Plus,
  X,
  Trash2,
  Edit2,
  AlertCircle,
  Car,
  Home,
  Wrench,
  TreePine,
  HelpCircle,
} from 'lucide-react';
import { createResource } from '../../lib/resources';
import { getResourceRoles, ResourceRole } from '../../lib/resourceRoles';

const setupInputClass =
  'w-full rounded-2xl border border-gray-200 bg-white px-4 h-[56px] text-[16px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 transition-shadow';

function SetupOptionCard({
  selected,
  onClick,
  icon,
  title,
  description,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  description?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-4 rounded-2xl border bg-white px-4 py-4 text-left shadow-[0_1px_3px_rgba(0,0,0,0.05)] transition-all active:scale-[0.99] ${
        selected
          ? 'border-gray-900 ring-1 ring-gray-900'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
      }`}
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100 text-gray-700">
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[16px] font-semibold text-gray-900">{title}</span>
        {description && <span className="block text-[13px] text-gray-500 mt-0.5">{description}</span>}
      </span>
      {selected && <CheckCircle className="h-5 w-5 text-gray-900 flex-shrink-0" />}
    </button>
  );
}

function SetupProgressBar({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  return (
    <div className="flex gap-1.5">
      {Array.from({ length: totalSteps }, (_, i) => {
        const stepNum = i + 1;
        const fill = currentStep > stepNum ? 1 : currentStep === stepNum ? 1 : 0;
        return (
          <div key={stepNum} className="h-1 flex-1 rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-gray-900 transition-all duration-300"
              style={{ width: `${fill * 100}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}


interface SetupStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
}

export default function Setup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [selectedServiceType, setSelectedServiceType] = useState<string | null>(null);
  const [setupData, setSetupData] = useState<{}>({});

  const [selectedTeamOption, setSelectedTeamOption] = useState<string | null>(null);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [createdTeamMembers, setCreatedTeamMembers] = useState<any[]>([]);
  const [justMeFormData, setJustMeFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    compensation_type: 'hourly' as 'hourly' | 'flat_rate' | 'commission',
    hourly_rate: '0',
    flat_rate_amount: '0',
    commission_percentage: '0',
    notes: ''
  });
  const [justMeFormLoading, setJustMeFormLoading] = useState(false);
  const [justMeFormError, setJustMeFormError] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [showServiceAreaForm, setShowServiceAreaForm] = useState(false);
  const [serviceAreas, setServiceAreas] = useState<any[]>([]);
  const [newServiceArea, setNewServiceArea] = useState({
    name: '',
    postal_codes: ['']
  });
  const [editingServiceArea, setEditingServiceArea] = useState<any | null>(null);
  const [deletingServiceArea, setDeletingServiceArea] = useState<any | null>(null);
  const [serviceAreaLoading, setServiceAreaLoading] = useState(false);
  const [serviceAreaError, setServiceAreaError] = useState<string | null>(null);

  // Business Information state
  const [businessInfo, setBusinessInfo] = useState({
    business_name: '',
    business_email: '',
    business_phone: '',
    business_address: ''
  });
  const [businessInfoLoading, setBusinessInfoLoading] = useState(false);
  const [businessInfoError, setBusinessInfoError] = useState<string | null>(null);

  // The step-2 inputs display fallbacks (businessName / email) when businessInfo
  // hasn't loaded yet. Sync those fallbacks INTO the state so the Continue
  // button's validation — and what saveBusinessInfo persists — always matches
  // what the user sees on screen.
  useEffect(() => {
    setBusinessInfo((prev) => {
      if (prev.business_name && prev.business_email) return prev;
      return {
        ...prev,
        business_name: prev.business_name || businessName || '',
        business_email: prev.business_email || email || '',
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessName, email]);

  // Final step state: 'self' | 'consultation', and whether the consultation
  // confirmation screen has been shown.
  const [finalStepSelection, setFinalStepSelection] = useState<string | null>(null);
  const [consultationConfirmed, setConsultationConfirmed] = useState(false);

  // Team member form state
  const [showTeamMemberForm, setShowTeamMemberForm] = useState(false);
  const [teamMemberLoading, setTeamMemberLoading] = useState(false);
  const [teamMemberError, setTeamMemberError] = useState<string | null>(null);
  const [permissionRoles, setPermissionRoles] = useState<ResourceRole[]>([]);
  const [teamMemberFormData, setTeamMemberFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: '',
    role_id: '',
    compensation_type: 'hourly' as 'hourly' | 'flat_rate' | 'commission',
    hourly_rate: '0',
    flat_rate_amount: '0',
    commission_percentage: '0',
    status: 'active',
    is_admin: false,
    password: '',
    confirm_password: '',
    notes: ''
  });

  // State for Apply Area to Team step
  const [mobileLoading, setMobileLoading] = useState<string | null>(null);

  // Dynamic setup steps based on service type
  const getSetupSteps = (): SetupStep[] => {
    // All flows now have the same simplified steps
      return [
        {
          id: 1,
          title: "Welcome",
          description: "Meet Nexus AI and get started",
          icon: <Bot className="h-6 w-6" />,
          completed: false
        },
        {
          id: 2,
          title: "Business Information",
          description: "Add your business details",
          icon: <Building2 className="h-6 w-6" />,
          completed: false
        },
        {
          id: 3,
          title: "Industry Selection",
          description: "Select your business industry",
          icon: <Building2 className="h-6 w-6" />,
          completed: false
        },
        {
          id: 4,
          title: "Service Type",
          description: "Select your service delivery method",
          icon: <Settings className="h-6 w-6" />,
          completed: false
        },
        {
          id: 5,
          title: "Completed Basic Setup",
          description: "Finish setup and enter the system",
          icon: <CheckCircle className="h-6 w-6" />,
          completed: false
        }
      ];
  };

  const setupSteps = getSetupSteps();

  useEffect(() => {
    const { userId: uid, email: userEmail } = location.state || {};
    if (!uid || !userEmail) {
      navigate('/login');
      return;
    }

    setUserId(uid);
    setEmail(userEmail);
    fetchBusinessName(uid);
    fetchBusinessId(uid);
  }, [location.state, navigate]);

  const fetchBusinessName = async (userId: string) => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('name')
        .eq('owner_id', userId)
        .single();

      if (error) {
        console.error('Error fetching business name:', error);
        return;
      }

      if (data) {
        setBusinessName(data.name);
      }
    } catch (err) {
      console.error('Error fetching business name:', err);
    }
  };

  const fetchBusinessId = async (userId: string) => {
    if (!supabase) return;
    
    try {
      console.log('Fetching business ID for userId:', userId);
      const { data, error } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_id', userId)
        .single();

      if (error) {
        console.error('Error fetching business ID:', error);
        return;
      }

      if (data) {
        console.log('Business ID fetched:', data.id);
        setBusinessId(data.id);
        // Load existing team members
        loadTeamMembers(data.id);
        // Load existing service areas
        loadServiceAreas(data.id);
        // Load business information
        loadBusinessInfo(userId);
      } else {
        console.error('No business found for userId:', userId);
      }
    } catch (err) {
      console.error('Error fetching business ID:', err);
    }
  };

  const loadBusinessInfo = async (userId: string) => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('name, email, phone, address')
        .eq('owner_id', userId)
        .single();

      if (error) {
        console.error('Error fetching business info:', error);
        return;
      }

      if (data) {
        setBusinessInfo({
          business_name: data.name || '',
          business_email: data.email || email || '',
          business_phone: data.phone || '',
          business_address: data.address || ''
        });
      }
    } catch (err) {
      console.error('Error fetching business info:', err);
    }
  };

  const saveBusinessInfo = async () => {
    if (!supabase || !businessId) return;
    
    setBusinessInfoLoading(true);
    setBusinessInfoError(null);
    
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          name: businessInfo.business_name,
          email: businessInfo.business_email,
          phone: businessInfo.business_phone,
          address: businessInfo.business_address
        })
        .eq('id', businessId);

      if (error) {
        console.error('Error saving business info:', error);
        setBusinessInfoError('Failed to save business information. Please try again.');
        return;
      }

      // Update the business name state as well
      setBusinessName(businessInfo.business_name);
      
    } catch (err) {
      console.error('Error saving business info:', err);
      setBusinessInfoError('Failed to save business information. Please try again.');
    } finally {
      setBusinessInfoLoading(false);
    }
  };

  const loadTeamMembers = async (businessId: string) => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('business_id', businessId)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading team members:', error);
        return;
      }

      setCreatedTeamMembers(data || []);
    } catch (err) {
      console.error('Error loading team members:', err);
    }
  };

  const loadServiceAreas = async (businessId: string) => {
    if (!supabase) return;
    
    try {
      setServiceAreaLoading(true);
      const { data, error } = await supabase
        .from('service_areas')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setServiceAreas(data || []);
    } catch (err) {
      console.error('Error loading service areas:', err);
      setServiceAreaError(err instanceof Error ? err.message : 'Failed to load service areas');
    } finally {
      setServiceAreaLoading(false);
    }
  };

  const handleAddPostalCode = () => {
    setNewServiceArea(prev => ({
      ...prev,
      postal_codes: [...prev.postal_codes, '']
    }));
  };

  const handleRemovePostalCode = (index: number) => {
    setNewServiceArea(prev => ({
      ...prev,
      postal_codes: prev.postal_codes.filter((_, i) => i !== index)
    }));
  };

  const handlePostalCodeChange = (index: number, value: string) => {
    // Limit to 3 digits and auto-capitalize
    const processedValue = value.slice(0, 3).toUpperCase();
    
    setNewServiceArea(prev => ({
      ...prev,
      postal_codes: prev.postal_codes.map((code, i) => 
        i === index ? processedValue : code
      )
    }));
  };

  const handleServiceAreaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId || !supabase) return;

    try {
      setServiceAreaLoading(true);
      setServiceAreaError(null);

      // Validate inputs
      if (!newServiceArea.name.trim()) {
        throw new Error('Service area name is required');
      }

      const validPostalCodes = newServiceArea.postal_codes.filter(code => code.trim());
      if (validPostalCodes.length === 0) {
        throw new Error('At least one postal code is required');
      }

      if (editingServiceArea) {
        // Update existing service area
        const { error: updateError } = await supabase
          .from('service_areas')
          .update({
            name: newServiceArea.name.trim(),
            postal_codes: validPostalCodes
          })
          .eq('id', editingServiceArea.id);

        if (updateError) throw updateError;
      } else {
        // Create service area
        const { error: createError } = await supabase
          .from('service_areas')
          .insert({
            business_id: businessId,
            name: newServiceArea.name.trim(),
            postal_codes: validPostalCodes
          });

        if (createError) throw createError;
      }

      // Reset form, close form, and reload service areas
      setNewServiceArea({ name: '', postal_codes: [''] });
      setEditingServiceArea(null);
      setShowServiceAreaForm(false);
      await loadServiceAreas(businessId);
    } catch (err) {
      console.error('Error creating/updating service area:', err);
      setServiceAreaError(err instanceof Error ? err.message : 'Failed to create/update service area');
    } finally {
      setServiceAreaLoading(false);
    }
  };

  const handleEditServiceArea = (serviceArea: any) => {
    setEditingServiceArea(serviceArea);
    setNewServiceArea({
      name: serviceArea.name,
      postal_codes: serviceArea.postal_codes.length > 0 ? serviceArea.postal_codes : ['']
    });
  };

  const handleCancelEdit = () => {
    setEditingServiceArea(null);
    setNewServiceArea({ name: '', postal_codes: [''] });
  };

  const handleDeleteServiceArea = async (serviceArea: any) => {
    if (!businessId || !supabase) return;

    try {
      setServiceAreaLoading(true);
      setServiceAreaError(null);

      const { error: deleteError } = await supabase
        .from('service_areas')
        .delete()
        .eq('id', serviceArea.id);

      if (deleteError) throw deleteError;

      // Close confirmation and reload service areas
      setDeletingServiceArea(null);
      await loadServiceAreas(businessId);
    } catch (err) {
      console.error('Error deleting service area:', err);
      setServiceAreaError(err instanceof Error ? err.message : 'Failed to delete service area');
    } finally {
      setServiceAreaLoading(false);
    }
  };

  const updateSetupStatus = async (step: number, completed: boolean = false) => {
    if (!userId || !supabase) return;

    try {
      await supabase
        .from('user_setup_status')
        .upsert({
          user_id: userId,
          setup_step: `step_${step}`,
          current_step: step,
          completed: completed,
          setup_data: setupData
        });
    } catch (err) {
      console.error('Error updating setup status:', err);
    }
  };

  const handleNextStep = async () => {
    // Save business information if we're on the business info step
    if (currentStep === 2) {
      await saveBusinessInfo();
    }

    // Re-persist industry when leaving the industry step so a missed click-save
    // (e.g. businessId not loaded yet) cannot leave the Automotive default.
    if (currentStep === 3 && selectedIndustry) {
      await persistBusinessIndustry(selectedIndustry);
    }
    
    if (currentStep < setupSteps.length) {
      await updateSetupStatus(currentStep, true);
      setCurrentStep(currentStep + 1);
    } else if (finalStepSelection === 'consultation' && !consultationConfirmed) {
      // First press after choosing the consultation: notify the team and show
      // the "a specialist will reach out" confirmation. The next press enters the app.
      setLoading(true);
      try {
        if (businessId && supabase) {
          const { data, error } = await supabase.functions.invoke('request-setup-consultation', {
            body: { businessId },
          });
          if (error || data?.error) {
            // Best-effort: don't block the user from entering the app.
            console.error('Consultation request failed:', error || data?.error);
          }
        }
      } finally {
        setLoading(false);
      }
      setConsultationConfirmed(true);
    } else {
      await completeSetup();
    }
  };

  const handlePreviousStep = async () => {
    // Leaving the consultation confirmation goes back to the choice, not step 4.
    if (currentStep === setupSteps.length && consultationConfirmed) {
      setConsultationConfirmed(false);
      return;
    }
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  /** Only Automotive maps to Automotive; every other setup choice → Home Services. */
  const toBusinessIndustryBucket = (selection: string): 'Automotive' | 'Home Services' =>
    selection.trim().toLowerCase() === 'automotive' ? 'Automotive' : 'Home Services';

  const resolveBusinessId = async (): Promise<string | null> => {
    if (businessId) return businessId;
    if (!supabase || !userId) return null;
    const { data, error } = await supabase
      .from('businesses')
      .select('id')
      .eq('owner_id', userId)
      .maybeSingle();
    if (error || !data?.id) {
      console.error('Error resolving business ID for industry save:', error);
      return null;
    }
    setBusinessId(data.id);
    return data.id;
  };

  const persistBusinessIndustry = async (industrySelection: string) => {
    if (!supabase) return;
    const id = await resolveBusinessId();
    if (!id) {
      console.error('Cannot save industry — business ID unavailable');
      return;
    }

    // Keep the specific UI choice in setup_data for analytics, but the
    // businesses.industry column only supports Automotive | Home Services.
    const dbIndustry = toBusinessIndustryBucket(industrySelection);

    try {
      console.log('📊 Saving industry selection:', industrySelection, '→', dbIndustry);
      const { error } = await supabase
        .from('businesses')
        .update({ industry: dbIndustry })
        .eq('id', id);

      if (error) {
        console.error('Error saving industry:', error);
      } else {
        console.log('✅ Industry saved successfully:', dbIndustry);
      }
    } catch (err) {
      console.error('Error saving industry:', err);
    }
  };

  const handleIndustrySelect = async (industry: string) => {
    setSelectedIndustry(industry);
    setSetupData((prev: any) => ({ ...prev, industry }));
    await persistBusinessIndustry(industry);
  };

  const handleServiceTypeSelect = async (serviceType: string) => {
    setSelectedServiceType(serviceType);
    setSetupData((prev: any) => ({ ...prev, serviceType }));

    // Configure calendar app and set as home screen
    if (!userId || !supabase) return;

    try {
      // Get the business ID for this user
      const { data: businessData, error: businessError } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_id', userId)
        .single();

      if (businessError || !businessData) {
        console.error('Error fetching business:', businessError);
        return;
      }

      console.log('📅 Configuring calendar for service type:', serviceType);

      // Check if calendar-core-app is installed
      const { data: calendarApp } = await supabase
        .from('business_apps_v2')
        .select('app_id')
        .eq('business_id', businessData.id)
        .eq('app_id', 'calendar-core-app')
        .single();

      // If calendar app is not installed, add it
      if (!calendarApp) {
        console.log('Installing calendar-core-app');
        await supabase
          .from('business_apps_v2')
          .insert({
            business_id: businessData.id,
            app_id: 'calendar-core-app',
            status: 'active',
            visible_app_grid: 3,
            hidden_app_grid: null,
            installed_at: new Date().toISOString()
          });
      }

      // Default home screen for new accounts is the Control Centre (the
      // helper clears any existing flag and inserts the registry row if the
      // business doesn't have one yet).
      const homeScreenResult = await setBusinessHomeScreen(businessData.id, CONTROL_CENTRE_APP_ID);
      if (!homeScreenResult.ok) {
        console.error('Error setting home screen:', homeScreenResult.error);
      } else {
        console.log('🏠 Home screen set to: control-centre-core-app');
      }

      // Drive the calendar's booking-mode toggles from the service type:
      //   Mobile   → mobile bookings on,  location bookings off
      //   Location → location bookings on, mobile bookings off
      //   Both     → both on
      // Same keys the Calendar app settings modal manages (set_app_setting RPC).
      const mobileEnabled = serviceType === 'Mobile' || serviceType === 'Both';
      const locationEnabled = serviceType === 'Location' || serviceType === 'Both';

      // New-account calendar defaults (written explicitly so they don't depend
      // on per-callsite read defaults): map view ON, sales map pins OFF,
      // bookings by service area OFF.
      const [mobileRes, locationRes, mapViewRes, salesPinsRes, serviceAreaRes] = await Promise.all([
        supabase.rpc('set_app_setting', {
          p_business_id: businessData.id,
          p_app_slug: 'calendar',
          p_setting_key: 'show_mobile_booking_in_fab',
          p_setting_value: mobileEnabled,
        }),
        supabase.rpc('set_app_setting', {
          p_business_id: businessData.id,
          p_app_slug: 'calendar',
          p_setting_key: 'show_location_booking_in_fab',
          p_setting_value: locationEnabled,
        }),
        supabase.rpc('set_app_setting', {
          p_business_id: businessData.id,
          p_app_slug: 'calendar',
          p_setting_key: 'map_view_enabled',
          p_setting_value: true,
        }),
        supabase.rpc('set_app_setting', {
          p_business_id: businessData.id,
          p_app_slug: 'calendar',
          p_setting_key: 'sales_pins_enabled',
          p_setting_value: false,
        }),
        supabase.rpc('set_app_setting', {
          p_business_id: businessData.id,
          p_app_slug: 'calendar',
          p_setting_key: 'mobile_booking_by_service_area',
          p_setting_value: false,
        }),
      ]);
      if (mobileRes.error) console.error('Error saving mobile booking toggle:', mobileRes.error);
      if (locationRes.error) console.error('Error saving location booking toggle:', locationRes.error);
      if (mapViewRes.error) console.error('Error saving map view default:', mapViewRes.error);
      if (salesPinsRes.error) console.error('Error saving sales pins default:', salesPinsRes.error);
      if (serviceAreaRes.error) console.error('Error saving service area default:', serviceAreaRes.error);
      console.log(`📅 Booking modes set — mobile: ${mobileEnabled}, location: ${locationEnabled}; map view on, sales pins off, service-area bookings off`);

    } catch (err) {
      console.error('Error configuring calendar settings:', err);
    }
  };

  const handleTeamOptionSelect = (option: string) => {
    setSelectedTeamOption(option);
      setShowTeamForm(true);
    setSetupData((prev: any) => ({ ...prev, teamOption: option }));
  };

  const handleJustMeFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId || !supabase) return;

    try {
      setJustMeFormLoading(true);
      setJustMeFormError(null);

      // Basic validation
      if (!justMeFormData.first_name.trim()) {
        throw new Error('First name is required');
      }
      if (!justMeFormData.last_name.trim()) {
        throw new Error('Last name is required');
      }
      if (!justMeFormData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(justMeFormData.email.trim())) {
        throw new Error('Valid email is required');
      }

      // Initialize compensation values
      const compensationData = {
        hourly_rate: 0,
        flat_rate_amount: 0,
        commission_percentage: 0,
        commission_rate: 0
      };

      // Set compensation value based on type
      switch (justMeFormData.compensation_type) {
        case 'hourly':
          const hourlyRate = parseFloat(justMeFormData.hourly_rate || '0');
          if (isNaN(hourlyRate)) {
            throw new Error('Please enter a valid hourly rate');
          }
          compensationData.hourly_rate = hourlyRate;
          break;
        case 'flat_rate':
          const flatRate = parseFloat(justMeFormData.flat_rate_amount || '0');
          if (isNaN(flatRate)) {
            throw new Error('Please enter a valid flat rate amount');
          }
          compensationData.flat_rate_amount = flatRate;
          break;
        case 'commission':
          const commissionRate = parseFloat(justMeFormData.commission_percentage || '0');
          if (isNaN(commissionRate) || commissionRate > 100) {
            throw new Error('Please enter a valid commission rate (0-100)');
          }
          compensationData.commission_percentage = commissionRate;
          compensationData.commission_rate = commissionRate;
          break;
      }

      // Create the resource payload
      const resourcePayload = {
        business_id: businessId,
        first_name: justMeFormData.first_name.trim(),
        last_name: justMeFormData.last_name.trim(),
        email: justMeFormData.email.trim(),
        phone: justMeFormData.phone.trim(),
        role: 'Team Member',
        status: 'active',
        compensation_type: justMeFormData.compensation_type,
        is_admin: false,
        password: 'tempPassword123', // Temporary password for setup
        confirm_password: 'tempPassword123', // Temporary password for setup
        last_active: new Date().toISOString(),
        earnings_ytd: 0,
        earnings_mtd: 0,
        earnings_last_month: 0,
        is_mobile_available: false,
        schedule_availability: {
          monday: { enabled: false, shifts: [] },
          tuesday: { enabled: false, shifts: [] },
          wednesday: { enabled: false, shifts: [] },
          thursday: { enabled: false, shifts: [] },
          friday: { enabled: false, shifts: [] },
          saturday: { enabled: false, shifts: [] },
          sunday: { enabled: false, shifts: [] }
        },
        ...compensationData
      };

      // Import createResource function
      const { createResource } = await import('../../lib/resources');
      await createResource(resourcePayload);

      // Add to created team members
      setCreatedTeamMembers((prev) => [...prev, {
        id: Date.now(), // Temporary ID
        first_name: justMeFormData.first_name.trim(),
        last_name: justMeFormData.last_name.trim(),
        email: justMeFormData.email.trim(),
        phone: justMeFormData.phone.trim(),
        compensation_type: justMeFormData.compensation_type,
        hourly_rate: compensationData.hourly_rate,
        flat_rate_amount: compensationData.flat_rate_amount,
        commission_percentage: compensationData.commission_percentage
      }]);

      // Reset form
      setJustMeFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        compensation_type: 'hourly',
        hourly_rate: '0',
        flat_rate_amount: '0',
        commission_percentage: '0',
        notes: ''
      });

      setShowTeamForm(false);
    } catch (err) {
      console.error('Error creating team member:', err);
      setJustMeFormError(err instanceof Error ? err.message : 'Failed to create team member');
    } finally {
      setJustMeFormLoading(false);
    }
  };



  const handleSkipStep = async () => {
    await handleNextStep();
  };

  const completeSetup = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🚀 Starting setup completion...', { userId, supabase: !!supabase });

      // Update setup status to completed
      if (userId && supabase) {
        console.log('📝 Updating setup status...');
        
        const { data: setupResponse, error: setupError } = await supabase
          .from('user_setup_status')
          .upsert({
            user_id: userId,
            setup_step: 'completed',
            current_step: setupSteps.length,
            completed: true,
            setup_data: setupData
          }, {
            onConflict: 'user_id'
          });

        if (setupError) {
          console.error('❌ Error updating setup status:', setupError);
          throw new Error(`Failed to update setup status: ${setupError.message}`);
        }

        console.log('✅ Setup marked as completed');
      } else {
        console.error('❌ Missing userId or supabase client');
        throw new Error('Missing required data for setup completion');
      }

      // Load business data and determine route (similar to login flow)
      if (userId && supabase) {
        try {
          console.log('🏢 Loading business data...');
          
          // Load businesses for the user
          const { data: businesses, error: businessError } = await supabase
            .from('businesses')
            .select('*')
            .eq('owner_id', userId)
            .order('created_at', { ascending: true });

          if (businessError) {
            console.error('❌ Error loading businesses:', businessError);
            throw new Error(`Failed to load business data: ${businessError.message}`);
          }

          if (!businesses || businesses.length === 0) {
            console.log('No businesses found for user');
            navigate(CONTROL_CENTRE_ROUTE);
          return;
        }

          // Use the first business
          const selectedBusiness = businesses[0];
          
          // Check if user needs to activate subscription (including trial)
          const trialActive =
            !!selectedBusiness.trial_enabled &&
            !!selectedBusiness.trial_end_date &&
            new Date(selectedBusiness.trial_end_date) > new Date();
          const isBusinessActive =
            stripeSyncedStatusAllowsNexusMainApp(
              selectedBusiness.subscription_status,
              selectedBusiness.subscription_past_due_at,
            ) ||
            trialActive;
          
          if (!isBusinessActive) {
            console.log('📋 User subscription is inactive and no active trial, redirecting to subscription activation');
            // Store business data for quick access
            localStorage.setItem('selectedBusiness', JSON.stringify(selectedBusiness));
            localStorage.setItem('businesses', JSON.stringify(businesses));
            // Redirect to subscription management to activate the plan
            navigate('/admin-subscription-management', { replace: true });
            return;
          }
          
          // If user has already paid (active subscription or trial), proceed to main app
          if (isBusinessActive) {
            console.log('✅ User has active subscription or trial, proceeding to main app');
          }
          
          // Store business data for quick access
          localStorage.setItem('selectedBusiness', JSON.stringify(selectedBusiness));
          localStorage.setItem('businesses', JSON.stringify(businesses));

          // New accounts land on the Control Centre — the same app the signup
          // flow just set as the business home screen.
          const targetRoute = CONTROL_CENTRE_ROUTE;

          // Set flags for smooth navigation
          localStorage.setItem('usePreloadedBusinessData', 'true');
          localStorage.setItem('skipInitializing', 'true');
          localStorage.setItem('preloadedRoute', targetRoute);
          // Open Nexus AI Set Up Assistant modal after landing
          sessionStorage.setItem('openAISetupAssistant', 'true');

          console.log('✅ Setup completed, redirecting to main app (tutorial disabled)');
          navigate(targetRoute, { replace: true });
          return;

        } catch (error) {
          console.error('❌ Error loading business data:', error);
          // Fallback to default route
          console.log('🔄 Falling back to Control Centre...');
          sessionStorage.setItem('openAISetupAssistant', 'true');
          navigate(CONTROL_CENTRE_ROUTE, { replace: true });
          return;
        }
      }

      // Fallback navigation
      sessionStorage.setItem('openAISetupAssistant', 'true');
      navigate(CONTROL_CENTRE_ROUTE, { replace: true });
    } catch (err) {
      console.error('❌ Error completing setup:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to complete setup: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="flex-1 flex flex-col justify-center pb-6">
            <h1 className="text-[32px] leading-[1.15] font-bold tracking-tight max-w-sm">
              Welcome to Nexus
            </h1>
            <p className="mt-3 text-[16px] text-gray-600 max-w-md">
              Hi! I&apos;m excited to help you set up{' '}
              <span className="font-semibold text-gray-900">{businessName || 'your business'}</span>.
              This will only take a minute.
            </p>
          </div>
        );

      case 2:
        return (
          <div className="flex-1 flex flex-col pb-6">
            <h1 className="mt-8 text-[32px] leading-[1.15] font-bold tracking-tight max-w-sm">
              Business details
            </h1>
            <p className="mt-3 text-[16px] text-gray-600">This info appears on invoices and forms.</p>

            {businessInfoError && (
              <div className="mt-4 bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-2xl text-[14px] flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {businessInfoError}
              </div>
            )}

            <div className="mt-8 space-y-3">
              <div>
                <label className="block text-[13px] font-medium text-gray-500 mb-1.5 px-1">Name</label>
                <input
                  type="text"
                  value={businessInfo.business_name || businessName || ''}
                  onChange={(e) => setBusinessInfo((prev) => ({ ...prev, business_name: e.target.value }))}
                  className={setupInputClass}
                  placeholder="Business name"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-gray-500 mb-1.5 px-1">Email</label>
                <input
                  type="email"
                  value={businessInfo.business_email || email || ''}
                  onChange={(e) => setBusinessInfo((prev) => ({ ...prev, business_email: e.target.value }))}
                  className={setupInputClass}
                  placeholder="Business email"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-gray-500 mb-1.5 px-1">Phone</label>
                <input
                  type="tel"
                  value={businessInfo.business_phone}
                  onChange={(e) => setBusinessInfo((prev) => ({ ...prev, business_phone: e.target.value }))}
                  className={setupInputClass}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-gray-500 mb-1.5 px-1">Address</label>
                <input
                  type="text"
                  value={businessInfo.business_address}
                  onChange={(e) => setBusinessInfo((prev) => ({ ...prev, business_address: e.target.value }))}
                  className={setupInputClass}
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="pb-6">
            <h1 className="mt-8 text-[32px] leading-[1.15] font-bold tracking-tight max-w-sm">
              What&apos;s your industry?
            </h1>
            <p className="mt-3 text-[16px] text-gray-600">We&apos;ll customize Nexus for you.</p>
            <div className="mt-8 space-y-3">
              {(
                [
                  { name: 'Automotive', description: 'Detailing, repair & maintenance', Icon: Car },
                  { name: 'Home Services', description: 'Cleaning, maintenance & more', Icon: Home },
                  { name: 'Trades', description: 'Electrical, plumbing, HVAC & construction', Icon: Wrench },
                  { name: 'Junk Removal', description: 'Hauling, cleanouts & disposal', Icon: Trash2 },
                  { name: 'Arborist', description: 'Tree care, trimming & removal', Icon: TreePine },
                  { name: 'Other', description: 'Any other service business', Icon: HelpCircle },
                ] as const
              ).map(({ name, description, Icon }) => (
                <SetupOptionCard
                  key={name}
                  selected={selectedIndustry === name}
                  onClick={() => handleIndustrySelect(name)}
                  icon={<Icon className="h-5 w-5" strokeWidth={1.75} />}
                  title={name}
                  description={description}
                />
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="pb-6">
            <h1 className="mt-8 text-[32px] leading-[1.15] font-bold tracking-tight max-w-sm">
              How do you serve customers?
            </h1>
            <p className="mt-3 text-[16px] text-gray-600">Select your service delivery method.</p>
            <div className="mt-8 space-y-3">
              <SetupOptionCard
                selected={selectedServiceType === 'Mobile'}
                onClick={() => handleServiceTypeSelect('Mobile')}
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                }
                title="Mobile"
                description="You travel to customer locations"
              />
              <SetupOptionCard
                selected={selectedServiceType === 'Location'}
                onClick={() => handleServiceTypeSelect('Location')}
                icon={<Building2 className="h-5 w-5" strokeWidth={1.75} />}
                title="Shop / Location"
                description="Customers come to your location"
              />
              <SetupOptionCard
                selected={selectedServiceType === 'Both'}
                onClick={() => handleServiceTypeSelect('Both')}
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                }
                title="Both"
                description="Mobile and location-based services"
              />
            </div>
          </div>
        );

      case 5:
        if (consultationConfirmed) {
          return (
            <div className="flex-1 flex flex-col justify-center pb-6">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 border border-emerald-100 mb-6">
                <CheckCircle className="h-7 w-7 text-emerald-600" />
              </div>
              <h1 className="text-[32px] leading-[1.15] font-bold tracking-tight max-w-sm">
                Consultation booked!
              </h1>
              <p className="mt-3 text-[16px] text-gray-600 max-w-md">
                A Nexus setup specialist will reach out shortly. In the meantime, feel free to explore the app.
              </p>
            </div>
          );
        }
        return (
          <div className="pb-6">
            <h1 className="mt-8 text-[32px] leading-[1.15] font-bold tracking-tight max-w-sm">
              Almost there!
            </h1>
            <p className="mt-3 text-[16px] text-gray-600 max-w-md">
              How would you like to proceed with your Nexus setup?
            </p>
            <div className="mt-8 space-y-3">
              <SetupOptionCard
                selected={finalStepSelection === 'self'}
                onClick={() => {
                  setFinalStepSelection('self');
                  setSetupData((prev: any) => ({ ...prev, setupChoice: 'self' }));
                }}
                icon={<Settings className="h-5 w-5" strokeWidth={1.75} />}
                title="I'll set it up myself"
                description="Jump in and configure services, team, and bookings at your own pace."
              />
              <SetupOptionCard
                selected={finalStepSelection === 'consultation'}
                onClick={() => {
                  setFinalStepSelection('consultation');
                  setSetupData((prev: any) => ({ ...prev, setupChoice: 'consultation' }));
                }}
                icon={<Bot className="h-5 w-5" strokeWidth={1.75} />}
                title="Book Setup Consultation"
                description="A Nexus specialist will contact you and set everything up with you."
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const loadPermissionRoles = async () => {
    if (!businessId) return;
    
    try {
      const roles = await getResourceRoles(businessId);
      setPermissionRoles(roles);
    } catch (err) {
      console.error('Error loading permission roles:', err);
      setTeamMemberError(err instanceof Error ? err.message : 'Failed to load permission roles');
    }
  };

  const handleTeamMemberFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔍 [DEBUG] Starting team member creation process');
    console.log('🔍 [DEBUG] Business ID:', businessId);
    console.log('🔍 [DEBUG] Form data:', teamMemberFormData);
    
    if (!businessId) {
      console.error('❌ [DEBUG] No business selected');
      setTeamMemberError('No business selected');
      return;
    }

    try {
      setTeamMemberLoading(true);
      setTeamMemberError(null);
      console.log('🔍 [DEBUG] Starting validation...');

      // Basic validation
      if (!teamMemberFormData.first_name.trim()) {
        console.error('❌ [DEBUG] First name validation failed');
        throw new Error('First name is required');
      }
      if (!teamMemberFormData.last_name.trim()) {
        console.error('❌ [DEBUG] Last name validation failed');
        throw new Error('Last name is required');
      }
      if (!teamMemberFormData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(teamMemberFormData.email.trim())) {
        console.error('❌ [DEBUG] Email validation failed:', teamMemberFormData.email);
        throw new Error('Valid email is required');
      }
      if (!teamMemberFormData.role.trim()) {
        console.error('❌ [DEBUG] Role validation failed');
        throw new Error('Job role is required');
      }
      if (!teamMemberFormData.password) {
        console.error('❌ [DEBUG] Password validation failed');
        throw new Error('Password is required');
      }

      // Validate password
      if (teamMemberFormData.password.length < 6) {
        console.error('❌ [DEBUG] Password length validation failed');
        throw new Error('Password must be at least 6 characters');
      }
      if (teamMemberFormData.password !== teamMemberFormData.confirm_password) {
        console.error('❌ [DEBUG] Password confirmation validation failed');
        throw new Error('Passwords do not match');
      }

      console.log('✅ [DEBUG] All validations passed');

      // Initialize compensation values
      const compensationData = {
        hourly_rate: 0,
        flat_rate_amount: 0,
        commission_percentage: 0,
        commission_rate: 0
      };

      // Set compensation based on type
      if (teamMemberFormData.compensation_type === 'hourly') {
        compensationData.hourly_rate = parseFloat(teamMemberFormData.hourly_rate) || 0;
        console.log('🔍 [DEBUG] Setting hourly rate:', compensationData.hourly_rate);
      } else if (teamMemberFormData.compensation_type === 'flat_rate') {
        compensationData.flat_rate_amount = parseFloat(teamMemberFormData.flat_rate_amount) || 0;
        console.log('🔍 [DEBUG] Setting flat rate amount:', compensationData.flat_rate_amount);
      } else if (teamMemberFormData.compensation_type === 'commission') {
        compensationData.commission_percentage = parseFloat(teamMemberFormData.commission_percentage) || 0;
        compensationData.commission_rate = compensationData.commission_percentage / 100;
        console.log('🔍 [DEBUG] Setting commission percentage:', compensationData.commission_percentage);
      }

      console.log('🔍 [DEBUG] Compensation data:', compensationData);

      // Create the resource payload
      const resourcePayload = {
        business_id: businessId,
        first_name: teamMemberFormData.first_name.trim(),
        last_name: teamMemberFormData.last_name.trim(),
        email: teamMemberFormData.email.trim(),
        phone: teamMemberFormData.phone.trim(),
        role: teamMemberFormData.role.trim(),
        role_id: teamMemberFormData.role_id || undefined,
        status: teamMemberFormData.status,
        compensation_type: teamMemberFormData.compensation_type,
        is_admin: teamMemberFormData.is_admin,
        password: teamMemberFormData.password,
        confirm_password: teamMemberFormData.confirm_password,
        earnings_ytd: 0,
        earnings_mtd: 0,
        earnings_last_month: 0,
        is_mobile_available: false,
        schedule_availability: {
          monday: { enabled: false, shifts: [] },
          tuesday: { enabled: false, shifts: [] },
          wednesday: { enabled: false, shifts: [] },
          thursday: { enabled: false, shifts: [] },
          friday: { enabled: false, shifts: [] },
          saturday: { enabled: false, shifts: [] },
          sunday: { enabled: false, shifts: [] }
        },
        ...compensationData
      };

      console.log('🔍 [DEBUG] Resource payload created:', resourcePayload);
      console.log('🔍 [DEBUG] About to call createResource...');

      const result = await createResource(resourcePayload);
      console.log('✅ [DEBUG] createResource completed successfully:', result);
      
      // Reset form and hide it
      setTeamMemberFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: '',
        role_id: '',
        compensation_type: 'hourly',
        hourly_rate: '0',
        flat_rate_amount: '0',
        commission_percentage: '0',
        status: 'active',
        is_admin: false,
        password: '',
        confirm_password: '',
        notes: ''
      });
      setShowTeamMemberForm(false);
      
      // Reload team members to show the newly created one
      if (businessId) {
        loadTeamMembers(businessId);
      }
      
      console.log('✅ [DEBUG] Team member created successfully');
    } catch (err) {
      console.error('❌ [DEBUG] Error creating resource:', err);
      console.error('❌ [DEBUG] Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        name: err instanceof Error ? err.name : undefined
      });
      setTeamMemberError(err instanceof Error ? err.message : 'Failed to create team member');
    } finally {
      console.log('🔍 [DEBUG] Setting loading to false');
      setTeamMemberLoading(false);
    }
  };

  const handleTeamMemberCreated = () => {
    setShowTeamMemberForm(false);
    // Reload team members to show the newly created one
    if (businessId) {
      loadTeamMembers(businessId);
    }
  };

  // Handler functions for Apply Area to Team step
  const handleMobileToggle = async (memberId: string) => {
    setMobileLoading(memberId);
    try {
      // TODO: Implement mobile toggle functionality
      console.log('Toggling mobile availability for member:', memberId);
      // This would update the member's is_mobile_available status
    } catch (error) {
      console.error('Error toggling mobile availability:', error);
    } finally {
      setMobileLoading(null);
    }
  };

  const handleWorkingHoursToggle = (memberId: string, day: string, enabled: boolean) => {
    // TODO: Implement working hours toggle functionality
    console.log('Toggling working hours for member:', memberId, 'day:', day, 'enabled:', enabled);
  };

  const handleWorkingHoursChange = (memberId: string, day: string, type: 'start' | 'end', value: string) => {
    // TODO: Implement working hours change functionality
    console.log('Changing working hours for member:', memberId, 'day:', day, 'type:', type, 'value:', value);
  };

  const handleServiceAreaToggle = (memberId: string, areaId: string, enabled: boolean) => {
    // TODO: Implement service area toggle functionality
    console.log('Toggling service area for member:', memberId, 'area:', areaId, 'enabled:', enabled);
  };

  if (!userId) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white"
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        }}
      >
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-500 text-[15px] font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const continueDisabled =
    loading ||
    (currentStep === 2 && (!businessInfo.business_name || !businessInfo.business_email)) ||
    (currentStep === 3 && !selectedIndustry) ||
    (currentStep === 4 && !selectedServiceType) ||
    (currentStep === 5 && !finalStepSelection && !consultationConfirmed);

  return (
    <div
      className="min-h-screen bg-white text-gray-900"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      <div className="max-w-xl mx-auto px-5 sm:px-6 flex flex-col min-h-screen">
        <div className="relative flex items-center justify-center py-5">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePreviousStep}
              className="absolute left-0 p-2 -ml-2 text-gray-500 hover:text-gray-800 transition-colors"
              aria-label="Back"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          <div className="text-xl font-extrabold tracking-tight">NexusAI</div>
        </div>

        <SetupProgressBar currentStep={currentStep} totalSteps={setupSteps.length} />

        {error && (
          <div className="mt-4 bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-2xl text-[14px] flex items-center gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="flex-1 flex flex-col">{renderStepContent()}</div>

        {currentStep !== 8 && (
          <div className="pb-8 pt-2">
            <button
              type="button"
              onClick={handleNextStep}
              disabled={continueDisabled}
              className="w-full flex items-center justify-center gap-2 rounded-full bg-gray-900 hover:bg-black px-6 h-[58px] text-[17px] font-semibold text-white transition-colors disabled:opacity-60"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Loading
                </>
              ) : (
                <>
                  {currentStep === setupSteps.length ? 'Get Started' : 'Continue'}
                  <ArrowRight className="h-5 w-5 text-emerald-300" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
